import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  UserPlus,
  ShoppingCart,
  TrendingUp,
  FileText,
  LogOut,
  Menu,
  X,
  Shield,
  Truck,
  AlertTriangle,
  ClipboardCheck,
  Boxes,
  BarChart3,
  Settings2,
  ShieldOff,
  Receipt,
  Undo2,
  ClipboardList,
  Layers,
  ScanLine,
  FileSpreadsheet,
  PackagePlus,
  Megaphone,
  MapPin,
  Folder,
  Tag,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const DashboardLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/assistant', icon: Sparkles, label: 'AI Assistant' },
    { path: '/products', icon: Package, label: 'Master Catalogue' },
    { path: '/suppliers', icon: Users, label: 'Suppliers' },
    { path: '/onboarding', icon: UserPlus, label: 'Onboarding' },
    { path: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders' },
    { path: '/po-approvals', icon: ClipboardCheck, label: 'PO Approvals' },
    { path: '/grn-dashboard', icon: Truck, label: 'GRN Dashboard' },
    { path: '/grn', icon: FileText, label: 'GRN History' },
    { path: '/vendor-mismatch', icon: AlertTriangle, label: 'Vendor Mismatches' },
    { path: '/purchase-returns', icon: Undo2, label: 'Purchase Returns' },
    { path: '/delivery-challans', icon: Truck, label: 'Delivery Challans' },
    { type: 'divider', label: 'Inventory' },
    { path: '/inventory', icon: BarChart3, label: 'Inventory Dashboard' },
    { path: '/inventory/stock-list', icon: Boxes, label: 'Stock Master' },
    { path: '/inventory/batches', icon: Layers, label: 'Batches (Multi-MRP)' },
    { path: '/inventory/low-stock', icon: AlertTriangle, label: 'Low Stock Report' },
    { path: '/stock-ledger', icon: FileText, label: 'Stock Ledger' },
    { path: '/stock-audit', icon: ScanLine, label: 'Stock Audit' },
    { type: 'divider', label: 'Sales & Billing' },
    { path: '/orders', icon: ClipboardList, label: 'Franchise Orders' },
    { path: '/product-requests', icon: PackagePlus, label: 'New Product Requests' },
    { path: '/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/sales-orders', icon: TrendingUp, label: 'Sales Orders' },
    { path: '/sales-orders/dashboard', icon: Package, label: 'SO Dashboard' },
    { type: 'divider', label: 'OG App' },
    { path: '/og-onboarding', icon: UserPlus, label: 'OG Onboarding' },
    { path: '/og-orders', icon: ClipboardList, label: 'OG Orders' },
    { path: '/og-publishing', icon: Megaphone, label: 'Product Publishing' },
    { path: '/og-category-publishing', icon: Folder, label: 'Category Publishing' },
    { path: '/og-offers-publishing', icon: Tag, label: 'Offers & New Arrivals' },
    { path: '/og-service-area', icon: MapPin, label: 'Service Area (PINs)' },
    { type: 'divider', label: 'Reports' },
    { path: '/reports', icon: FileSpreadsheet, label: 'Reports' },
    { path: '/franchise-performance', icon: TrendingUp, label: 'Franchise Performance' },
  ];
  
  // Add admin-only items
  if (user && (user.role === 'admin' || user.role === 'manager')) {
    menuItems.push({ type: 'divider', label: 'Accounts' });
    menuItems.push({ path: '/accounts/vendor-payments', icon: Wallet, label: 'Vendor Payment Summary' });
    menuItems.push({ path: '/accounts/credit-notes', icon: Receipt, label: 'Credit Notes' });
    menuItems.push({ path: '/accounts/debit-notes', icon: FileText, label: 'Debit Notes' });
    menuItems.push({ path: '/accounts/invoice-edits', icon: Shield, label: 'Invoice Edit History' });
    menuItems.push({ path: '/accounts/dc-edits', icon: Truck, label: 'DC Edit History' });
    menuItems.push({ type: 'divider', label: 'Admin' });
    if (user.role === 'admin') {
      menuItems.push({ path: '/inventory/adjustment', icon: Settings2, label: 'Stock Adjustment' });
    }
    menuItems.push({ path: '/order-control', icon: ShieldOff, label: 'Order Control' });
    if (user.role === 'admin') {
      menuItems.push({ path: '/admin-settings', icon: Shield, label: 'Admin Settings' });
    }
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 sidebar-dark transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700">
            <h1 className="text-xl font-heading font-bold text-white" data-testid="app-title">Warehouse ERP</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {menuItems.map((item, idx) => {
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${item.label}-${idx}`} className="pt-4 pb-2 px-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{item.label}</p>
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-white" data-testid="user-name">{user.name}</p>
                <p className="text-xs text-slate-400" data-testid="user-role">{user.role}</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 glassmorphism border-b border-slate-200 sticky top-0 z-40">
          <div className="flex items-center justify-between h-full px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-700 hover:text-slate-900"
              data-testid="open-sidebar-btn"
            >
              <Menu size={24} />
            </button>
            <div className="hidden lg:block" />
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
